import serviceApi from 'services/api';
import auth from 'services/auth';
import { CardProps, IntegrationCardDetails } from './card-service';
import { IntegrationProps } from './integration-service';
import ReactGA from 'react-ga4';

const api = process.env.REACT_APP_ENVIRONMENT === 'local' ? auth : serviceApi;

export type OccupantProps = {
  uuid?: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  isVisitor?: boolean;
  isStaff?: boolean;
  organizationUuid: string;
  buildingUuid: string | any;
  accountUuid: string | any;
  fcmToken: string;
  credentialId: string;
  cards?: CardProps[] | any;
  badgeId?: string;
  facilityCode?: string;
  description?: string | any;
  integrationUuid?: string;
  integration?: IntegrationProps[] | any;
  latchIntegrationUuid?: string;
  email?: string;
  startTime?: any;
  endTime?: any;
  format?: string;
  createdAt?: any;
  doorIds?: string[] | [];
  startDate?: any;
  endDate?: any;
  lostPhoneId?: string;
  latchDoorIds?: string[] | [];
  latchStartDate?: any;
  latchEndDate?: any;
  integrationCardDetails?: IntegrationCardDetails;
};

/* The function `findAllOccupants` is an asynchronous function that makes a GET request to the `/apiv1/admin/occupants` endpoint and returns 
all the Occupants data from the response. */
const findAllOccupants = async () => {
  const response = await api.get<OccupantProps[]>('/apiv1/admin/occupants');
  return response?.data;
};

/* The function `findOccupantsById` retrieves an Occupant object from an API based on the provided UUID. */
const findOccupantsById = async (uuid: string | undefined) => {
  const response = await api.get<OccupantProps>(`/apiv1/admin/occupant/${uuid}`);
  return response?.data;
};

/* The function creates a new Occupant by making a POST request to the '/apiv1/admin/occupant' endpoint with the provided Occupant data. */
const createOccupant = async (newOccupant: OccupantProps) => {
  ReactGA.event({
    category: 'User',
    action: 'Create Occupant',
    label: `OrgUuid: ${newOccupant.organizationUuid}, BuildingUuid: ${
      newOccupant.buildingUuid
    }, DoorIds: ${JSON.stringify(newOccupant.doorIds)}`
  });
  const response = await api.post<OccupantProps>('/apiv1/admin/occupant', newOccupant);
  return response?.data;
};

/* The function `updateOccupant` is an asynchronous function that updates an Occupant using a PUT request to an API endpoint. */
const updateOccupant = async (uuid: string | undefined, updatedOccupant: OccupantProps) => {
  ReactGA.event({
    category: 'User',
    action: 'Update Occupant',
    label: `occupantUuid: ${uuid}, OrgUuid: ${updatedOccupant.organizationUuid}, BuildingUuid: ${
      updatedOccupant.buildingUuid
    }, DoorIds: ${JSON.stringify(updatedOccupant.doorIds)}`
  });
  const response = await api.put<OccupantProps>(`/apiv1/admin/occupant/${uuid}`, updatedOccupant);
  return response?.data;
};

/* The function `deleteOccupant` is an asynchronous function that deletes an Occupant by its UUID using an API call. */
const deleteOccupant = async (uuid: string | undefined) => {
  ReactGA.event({
    category: 'User',
    action: 'Delete Occupant',
    label: `occupantUuid: ${uuid}`
  });
  const response = await api.delete<OccupantProps>(`/apiv1/admin/occupant/${uuid}`);
  return response?.data;
};

/* The function `importOccupantsCSV` is an asynchronous function that sends a POST request to an API endpoint with a 
`newOccupantCSV` containing `accountUuid`, `buildingUuid`, and `integrationUuid` and returns the response data. */
const importOccupantsCSV = async (newOccupantCSV: any) => {
  ReactGA.event({
    category: 'User',
    action: 'Import Occupants CSV'
  });
  const response = await api.post<any>(
    `/apiv1/admin/occupants/upload?account=${newOccupantCSV.accountUuid}&building=${newOccupantCSV.buildingUuid}&integration=${newOccupantCSV.integrationUuid}`,
    newOccupantCSV.row
  );
  return response?.data;
};

const OccupantService = {
  findAllOccupants,
  createOccupant,
  findOccupantsById,
  updateOccupant,
  deleteOccupant,
  importOccupantsCSV
};

export default OccupantService;
